import { LinkNoPrefetch } from '@/components/Link'
import { SideNav } from '@/components/nsw/side-nav/SideNav'
import {
	Focusarea,
	Focusareaoption,
	Optionslist,
} from '@/kontent/content-types'
import { TaxoStageWithLifeSkill, TaxoStageYearWithLifeSkill } from '@/types'
import { FocusareaOrOptionListOrFocusareoptionExtended } from '@/types/customKontentTypes'
import { getSlugByCodename } from '@/utils'
import { getSlugByCodenameYear } from '@/utils/slug-year-utils'
import {
	isFocusarea,
	isFocusareaoption,
	isOptionList,
} from '@/utils/type_predicates'
import { IContentItemsContainer } from '@kontent-ai/delivery-sdk'
import { useId } from 'react'

export interface ContentSideNavProps {
	focusAreas: FocusareaOrOptionListOrFocusareoptionExtended[]
	currentFocusAreaCodename: Focusarea['system']['codename']
	onNavItemClick?: (
		_e,
		_focusArea: Focusarea | Optionslist | Focusareaoption,
	) => void
	currentSyllabusCodename: string
	currentStage: TaxoStageWithLifeSkill
	currentYear: TaxoStageYearWithLifeSkill
	qsOnUrl?: string[]
	shallowLinks?: boolean
	preview?: boolean
	linkedItems: IContentItemsContainer
}

export const getUrlFromFocusArea = (
	focusArea: FocusareaOrOptionListOrFocusareoptionExtended & {
		url_slug?: string
	},
	currentSyllabusCodename: string,
	currentStage?: TaxoStageWithLifeSkill,
	currentYear?: TaxoStageYearWithLifeSkill,
	qs: string[] = [],
	slugAfterFocusAreaSlug: string = '',
	linkedItems?: IContentItemsContainer,
) => {
	const learningAreaSlug = getSlugByCodename(
		focusArea.elements.key_learning_area_default?.value?.[0]?.codename ||
			'',
	)
	const syllabusSlug = getSlugByCodename(currentSyllabusCodename)

	const focusAreaSlug = getSlugByCodename(focusArea.system.codename)

	let optionlistCodename = ''
	if (isFocusareaoption(focusArea)) {
		const optsList = Object.entries(linkedItems || {}).find(
			([, optionList]) => {
				if (isOptionList(optionList)) {
					return optionList.elements.focus_area_options.value.includes(
						focusArea.system.codename,
					)
				}
				return false
			},
		)
		optionlistCodename =
			optsList && optsList.length > 0 ? optsList[0] : undefined
	}

	let tmpPath = [
		'/learning-areas',
		`/${learningAreaSlug}`,
		`/${syllabusSlug}`,
		'/content/',
		`${
			focusArea.url_slug
				? focusArea.url_slug
				: getSlugByCodenameYear(currentYear || currentStage)
		}`, // stage or year
	]

	if (isFocusarea(focusArea) || isOptionList(focusArea)) {
		tmpPath = [
			...tmpPath,
			`/${focusAreaSlug}`,
			`${slugAfterFocusAreaSlug}`,
			`${qs.length ? '?' + qs.join('&') : ''}`,
		]
	} else {
		tmpPath = [
			...tmpPath,
			optionlistCodename
				? `/${getSlugByCodename(optionlistCodename)}`
				: '', // optionlist
			`/${focusAreaSlug}`, // focus area / focus area option slug
			`${slugAfterFocusAreaSlug}`,
			`${qs.length ? '?' + qs.join('&') : ''}`,
		].filter(Boolean)
	}
	return tmpPath.join('')
}

export const getFirstFocusAreaOptionUrlFromOptionlist = (
	optionList: Optionslist,
	currentSyllabusCodename: string,
	currentStage?: TaxoStageWithLifeSkill,
	currentYear?: TaxoStageYearWithLifeSkill,
	qs: string[] = [],
) => {
	const firstFocusareaOptionSlug = getSlugByCodename(
		optionList.elements.focus_area_options.value[0] || '',
	)
	return getUrlFromFocusArea(
		optionList,
		currentSyllabusCodename,
		currentStage,
		currentYear,
		qs,
		`/${firstFocusareaOptionSlug}`,
	)
}

export const getFocusAreaOptionUrl = (
	focusAreaOption: Focusareaoption,
	optionList: Optionslist,
	currentSyllabusCodename: string,
	currentStage?: TaxoStageWithLifeSkill,
	currentYear?: TaxoStageYearWithLifeSkill,
	qs: string[] = [],
) => {
	const focusAreaOptionSlug = getSlugByCodename(
		focusAreaOption.system.codename,
	)
	return getUrlFromFocusArea(
		optionList,
		currentSyllabusCodename,
		currentStage,
		currentYear,
		qs,
		`/${focusAreaOptionSlug}`,
	)
}

export const ContentSideNav = ({
	focusAreas,
	onNavItemClick,
	currentFocusAreaCodename,
	currentSyllabusCodename,
	currentStage,
	currentYear,
	qsOnUrl = [],
	shallowLinks = false,
	preview = false,
	linkedItems,
}: ContentSideNavProps) => {
	const uniqueId = useId()
	return (
		<>
			<div id={'content-sidenav-sticky-indicator-' + uniqueId} />
			<SideNav
				className="sticky top-0"
				css={{
					'.is-preview &': {
						top: 26,
					},
					'.nsw-side-nav__item-link': {
						width: '100%',
						textAlign: 'left',
						fontWeight: 'var(--nsw-font-normal)',
						display: 'block',
						textDecoration: 'none',
						color: 'var(--nsw-text-dark)',
						padding: '1rem',

						'&.current': {
							backgroundColor: 'var(--nsw-off-white)',
							fontWeight: 'bold',
						},
					},
				}}
				items={focusAreas.map((focusArea) => {
					const href = isOptionList(focusArea)
						? getFirstFocusAreaOptionUrlFromOptionlist(
								focusArea,
								currentSyllabusCodename,
								currentStage,
								currentYear,
								qsOnUrl,
						  )
						: getUrlFromFocusArea(
								focusArea,
								currentSyllabusCodename,
								currentStage,
								currentYear,
								qsOnUrl,
								'',
								linkedItems,
						  )

					return {
						tag: onNavItemClick ? 'button' : LinkNoPrefetch,
						text: focusArea.elements.title.value,
						href: onNavItemClick ? undefined : href,
						onClick: onNavItemClick
							? (e) => {
									onNavItemClick(e as any, focusArea)
							  }
							: undefined,
						isActive:
							focusArea.system.codename ===
							currentFocusAreaCodename,
						shallow: shallowLinks,
						scroll: false,
						prefetch: !preview,
					}
				})}
			/>
		</>
	)
}
